import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Truck, User, Package, MapPin, Calendar, ChevronRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceRequestPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    origin_city: "",
    origin_state: "",
    destination_city: "",
    destination_state: "",
    cargo_type: "",
    cargo_description: "",
    estimated_weight: "",
    special_requirements: "",
  });

  const cargoTypes = [
    { value: "general", label: "General Freight" },
    { value: "palletized", label: "Palletized" },
    { value: "boxes", label: "Boxes/Packages" },
    { value: "furniture", label: "Furniture" },
    { value: "equipment", label: "Industrial Equipment" },
    { value: "other", label: "Other" },
  ];

  const usStates = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
    "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
    "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
    "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
    "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
    "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
    "Wisconsin", "Wyoming"
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error("Please select a pickup date");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        pickup_date: format(date, "yyyy-MM-dd"),
        estimated_weight: formData.estimated_weight ? parseFloat(formData.estimated_weight) : null,
      };

      await axios.post(`${API_URL}/api/service-requests`, submitData);
      
      setSubmitted(true);
      toast.success("Request submitted successfully!");
    } catch (error) {
      console.error("Error submitting request:", error);
      toast.error(error.response?.data?.detail || "Error submitting request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-20" data-testid="success-message">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-emerald-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="heading-2 text-slate-900 mb-4">Request Submitted!</h1>
          <p className="text-slate-600 mb-8">
            We have received your service request. Our team will contact you 
            within the next 24 hours with a quote.
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold uppercase tracking-wider px-8 py-4"
            data-testid="back-home-button"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="service-request-page">
      {/* Header */}
      <section className="bg-slate-900 py-20">
        <div className="section-container">
          <p className="text-orange-500 font-bold uppercase tracking-widest mb-4 text-sm">
            Request Service
          </p>
          <h1 className="heading-1 text-white">
            Get a Quote
          </h1>
          <p className="text-slate-400 mt-4 text-lg max-w-xl">
            Fill out the form and receive a personalized quote in less than 24 hours.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="section-container">
          <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
            {/* Contact Information */}
            <div className="bg-white p-8 mb-8 shadow-sm border border-slate-200" data-testid="contact-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-orange-100 p-2 rounded-sm">
                  <User className="h-5 w-5 text-orange-600" />
                </div>
                <h2 className="heading-3 text-slate-900">Contact Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company_name" className="input-label">Company Name *</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. ABC Logistics"
                    className="h-12"
                    data-testid="input-company-name"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_name" className="input-label">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. John Smith"
                    className="h-12"
                    data-testid="input-contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="input-label">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="email@company.com"
                    className="h-12"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="input-label">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="(123) 456-7890"
                    className="h-12"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            </div>

            {/* Route Information */}
            <div className="bg-white p-8 mb-8 shadow-sm border border-slate-200" data-testid="route-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-orange-100 p-2 rounded-sm">
                  <MapPin className="h-5 w-5 text-orange-600" />
                </div>
                <h2 className="heading-3 text-slate-900">Origin & Destination</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Origin */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 uppercase text-sm tracking-wider">Origin</h3>
                  <div>
                    <Label htmlFor="origin_city" className="input-label">City *</Label>
                    <Input
                      id="origin_city"
                      name="origin_city"
                      value={formData.origin_city}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. Boston"
                      className="h-12"
                      data-testid="input-origin-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="origin_state" className="input-label">State *</Label>
                    <Select
                      value={formData.origin_state}
                      onValueChange={(value) => handleSelectChange("origin_state", value)}
                    >
                      <SelectTrigger className="h-12" data-testid="select-origin-state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {usStates.map((state) => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Destination */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700 uppercase text-sm tracking-wider">Destination</h3>
                  <div>
                    <Label htmlFor="destination_city" className="input-label">City *</Label>
                    <Input
                      id="destination_city"
                      name="destination_city"
                      value={formData.destination_city}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g. Los Angeles"
                      className="h-12"
                      data-testid="input-destination-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="destination_state" className="input-label">State *</Label>
                    <Select
                      value={formData.destination_state}
                      onValueChange={(value) => handleSelectChange("destination_state", value)}
                    >
                      <SelectTrigger className="h-12" data-testid="select-destination-state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {usStates.map((state) => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Cargo Information */}
            <div className="bg-white p-8 mb-8 shadow-sm border border-slate-200" data-testid="cargo-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-orange-100 p-2 rounded-sm">
                  <Package className="h-5 w-5 text-orange-600" />
                </div>
                <h2 className="heading-3 text-slate-900">Cargo Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="cargo_type" className="input-label">Cargo Type *</Label>
                  <Select
                    value={formData.cargo_type}
                    onValueChange={(value) => handleSelectChange("cargo_type", value)}
                  >
                    <SelectTrigger className="h-12" data-testid="select-cargo-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {cargoTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="estimated_weight" className="input-label">Estimated Weight (lbs)</Label>
                  <Input
                    id="estimated_weight"
                    name="estimated_weight"
                    type="number"
                    value={formData.estimated_weight}
                    onChange={handleInputChange}
                    placeholder="e.g. 5000"
                    className="h-12"
                    data-testid="input-weight"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="cargo_description" className="input-label">Cargo Description</Label>
                  <Textarea
                    id="cargo_description"
                    name="cargo_description"
                    value={formData.cargo_description}
                    onChange={handleInputChange}
                    placeholder="Briefly describe the merchandise to be transported..."
                    rows={3}
                    data-testid="input-cargo-description"
                  />
                </div>
              </div>
            </div>

            {/* Schedule Information */}
            <div className="bg-white p-8 mb-8 shadow-sm border border-slate-200" data-testid="schedule-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-orange-100 p-2 rounded-sm">
                  <Calendar className="h-5 w-5 text-orange-600" />
                </div>
                <h2 className="heading-3 text-slate-900">Pickup Date</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="input-label">Desired Pickup Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-12 justify-start text-left font-normal border-slate-300"
                        data-testid="date-picker-trigger"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                        data-testid="calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="special_requirements" className="input-label">Special Requirements</Label>
                  <Textarea
                    id="special_requirements"
                    name="special_requirements"
                    value={formData.special_requirements}
                    onChange={handleInputChange}
                    placeholder="Any special requirements? e.g. fragile cargo, specific time window..."
                    rows={3}
                    data-testid="input-special-requirements"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm font-bold uppercase tracking-wider px-12 py-6 text-base shadow-lg hover:shadow-orange-500/30 transition-all duration-200 disabled:opacity-50"
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  "Submitting..."
                ) : (
                  <>
                    Submit Request
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
              <p className="text-slate-500 text-sm mt-4">
                By submitting this form, you agree to our terms of service.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ServiceRequestPage;
